import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import API from "../../../util/api";
import {
  adminTable,
  adminTableHeaderCell,
  adminTableDataCell,
} from "../common/AdminTableStyles";

const breakpoints = {
  tablet: 768,
  phone:  480,
};

const Wrapper = styled.div`
  width: 100%;

  @media (max-width: ${breakpoints.phone}px) {
    width: 750px;
    margin: 0 auto;
  }
`;

const Table = styled.table`
  ${adminTable};
  font-size: 14px;

  thead th {
    ${adminTableHeaderCell};
    background-color: #fff;
    position: relative;

    .sort-icons {
      position: absolute;
      right: 10px;
      top: 50%;
      transform: translateY(-50%);
      display: flex;
      flex-direction: column;
      font-size: 10px;

      span {
        cursor: pointer;
        line-height: 10px;
        color: #9CA3AF;
        display: block;
      }

      .active {
        color: #A855F7;
      }
    }
  }

  tbody td {
    ${adminTableDataCell};
  }

  b {
    font-weight: 600;
  }

  .userCell,
  .multiLine {
    white-space: pre-line;
  }
`;

const ActionsCell = styled.td`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  strong {
    color: ${({ colors }) => colors.labelText};
    line-height: 1.2;
  }
`;

const LinkButton = styled.button`
  margin-left: 8px;
  padding: 5px 10px;
  border: none;
  border-radius: 5px;
  font-size: 14px;
  background: ${({ colors }) => colors.buttonBackground};
  color: #fff;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

export default function POCUpdatesContent({ colors }) {
  const [pocs, setPocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  useEffect(() => {
    const loadPOCs = async () => {
      setLoading(true);
      try {
        const resp = await API.getPOC({});
        setPocs(resp.pocs || []);
      } catch (err) {
        console.error("Failed to load POCs:", err);
      } finally {
        setLoading(false);
      }
    };
    loadPOCs();
  }, []);

  const sortedData = useMemo(() => {
    if (!sortKey) return pocs;
    return [...pocs].sort((a, b) => {
      const aVal = String(a[sortKey] || "").toLowerCase();
      const bVal = String(b[sortKey] || "").toLowerCase();
      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [pocs, sortKey, sortOrder]);

  const handleSort = (key, order) => {
    setSortKey(key);
    setSortOrder(order);
  };

  const handleApprove = async (customer_id) => {
    try {
      await API.approvePOC({ customer_id });
      const resp = await API.getPOC({});
      setPocs(resp.pocs || []);
    } catch (err) {
      console.error("Approve failed:", err);
    }
  };

  const handleReject = async (customer_id) => {
    try {
      await API.rejectPOC({ customer_id });
      const resp = await API.getPOC({});
      setPocs(resp.pocs || []);
    } catch (err) {
      console.error("Reject failed:", err);
    }
  };

  if (loading) return <p>Loading…</p>;

  return (
    <Wrapper>
      <h1 style={{ color: colors.labelText }}>Updated points of contact</h1>
      <Table colors={colors}>
        <thead>
          <tr>
            {[
              { key: "date",          label: "Date" },
              { key: "org_name",      label: "Org" },
              { key: "customer_code", label: "Code" },
              { key: "name",          label: "Name" },
              { key: "phone",         label: "Phone" },
              { key: "email",         label: "Email" },
              { key: null,            label: "" },
            ].map(({ key, label }) => {
              const isAscActive = key && sortKey === key && sortOrder === "asc";
              const isDescActive = key && sortKey === key && sortOrder === "desc";
              const defaultColor = "#9CA3AF";
              const activeColor = "#A855F7";
              return (
                <th key={label}>
                  {label}
                  {key && (
                    <div className="sort-icons">
                      <span
                        onClick={() => handleSort(key, "asc")}
                        className={isAscActive ? "active" : ""}
                        style={{
                          color: isAscActive ? activeColor : defaultColor,
                          display: isDescActive ? "none" : "block"
                        }}
                      >
                        ▲
                      </span>
                      <span
                        onClick={() => handleSort(key, "desc")}
                        className={isDescActive ? "active" : ""}
                        style={{
                          color: isDescActive ? activeColor : defaultColor,
                          display: isAscActive ? "none" : "block"
                        }}
                      >
                        ▼
                      </span>
                    </div>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((item, idx) => {
            return (
              <tr key={idx}>
                <td>{item.date.split(" ")[0]}</td>
                <td>{item.org_name}</td>
                <td>{item.customer_code}</td>
                <td className="multiLine">
                  <b>New: <span style={{ color: colors.buttonBackground }}>{item.name}</span></b><br/>
                  <b>Current:</b> {item.old_name}
                </td>
                <td className="multiLine">
                  <b>New: <span style={{ color: colors.buttonBackground }}>{item.phone}</span></b><br/>
                  <b>Current:</b> {item.old_phone}
                </td>
                <td className="multiLine">
                  <b>New: <span style={{ color: colors.buttonBackground }}>{item.email}</span></b><br/>
                  <b>Current:</b> {item.old_email}
                </td>
                <ActionsCell colors={colors}>
                  <LinkButton colors={colors} onClick={() => handleApprove(item.customer_id)}>
                    Approve
                  </LinkButton>
                  <LinkButton colors={colors} onClick={() => handleReject(item.customer_id)}>
                    Reject
                  </LinkButton>
                </ActionsCell>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Wrapper>
  );
}
